/* jshint esversion: 6 */
import { useCallback, useContext, useEffect, useState } from "react";
import { MyContext } from "../../Context/MyProvider";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ThemedPageFrame from "./ThemedPageFrame";

const BusinessUser = () => {
    const { t } = useTranslation();

    const { BaseUrl } = useContext(MyContext);
    const [data, setData] = useState([]);
    const [videosData, setVideosData] = useState([]);
    const [businessOrgData, setBusinessOrgData] = useState([]);
    const [formList, setFormList] = useState([]);
    const [activeVideoId, setActiveVideoId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const getFormData = async () => {
            try {
                const res = await axios.post(`${BaseUrl}user_api/form_list.php`);

                if (res?.data?.status) {
                    const filteredData = (res.data.data || []).filter(
                        (item) => item.form_type === "business"
                    );

                    setFormList(filteredData);
                } else {
                    setFormList([])
                }
            } catch (error) {
                setFormList([])
            }
        };

        getFormData();
    }, [BaseUrl]);

    useEffect(() => {
        const getBusinessOrgData = async () => {
            try {
                const res = await axios.get(`${BaseUrl}user_api/register_with_us.php`);

                setBusinessOrgData(res.data.data || []);
            } catch (error) {
                setBusinessOrgData([])
            }
        };

        getBusinessOrgData();
    }, [BaseUrl]);

    useEffect(() => {
        const getVideosData = async () => {
            try {
                const res = await axios.get(`${BaseUrl}user_api/business_user_videos.php`);

                if (res?.data?.status) {
                    setVideosData(res.data.data);
                } else {
                    setVideosData([])
                }
            } catch (error) {
                setVideosData([])
            }
        };

        getVideosData();
    }, [BaseUrl]);

    const handleBanner = useCallback(() => {
        const Userid = localStorage.getItem("Userid") || "0";

        if (Userid) {
            axios.get(`${BaseUrl}user_api/get_banner.php`)
                .then((res) => {
                    const bannerData = Array.isArray(res?.data?.data) ? res.data.data : [];

                    // filter only business type
                    const businessData = bannerData?.filter(item => item.banner_type === "business");

                    setData(businessData);
                })
                .catch((err) => {
                    setData([]);
                });
        }
    }, [BaseUrl]);

    useEffect(() => {
        handleBanner();
    }, [handleBanner]);

    const handleVideoSound = (videoId) => {
        setActiveVideoId((currentVideoId) =>
            currentVideoId === videoId ? null : videoId
        );
    };

    const businessBanners = Array.isArray(data) ? data : [];
    const businessVideos = Array.isArray(videosData) ? videosData : [];
    const businessOptions = Array.isArray(businessOrgData) ? businessOrgData : [];
    const businessForms = Array.isArray(formList) ? formList : [];
    const verticalVideos = businessVideos.filter((item) => item?.type?.toLowerCase() === "vertical");
    const horizontalVideos = businessVideos.filter((item) => item?.type?.toLowerCase() === "horizontal");
    const businessOptionCount = businessBanners.length + businessVideos.length + businessOptions.length + businessForms.length;

    return (
        <ThemedPageFrame
            activePath="/business-user"
            eyebrow="NabhiVerse Business"
            title={t("Business User")}
            subtitle={t("Register, promote, and grow with NabhiVerse business services.")}
            summary={<><span>{t("Options")}</span><strong>{businessOptionCount}</strong></>}
            className="legacy_themed_page business_page_layout"
        >
            {businessBanners.length > 0 && (
                <section className="business_banner_section">
                    <div className="business_section_head">
                        <span>{t("Featured")}</span>
                        <h2>{t("Business Banners")}</h2>
                    </div>
                    <Swiper
                        className="desktop_hero_swiper business_banner_swiper"
                        modules={[Autoplay, Navigation, Pagination]}
                        navigation={businessBanners.length > 1}
                        pagination={{ clickable: true }}
                        loop={businessBanners.length > 1}
                        autoplay={businessBanners.length > 1 ? { delay: 3500, disableOnInteraction: false } : false}
                    >
                        {businessBanners.map((item, index) => (
                            <SwiperSlide key={item.id || index}>
                                <div className="desktop_hero has_image business_banner_slide">
                                    <img src={item?.image} alt={item?.title || ""} />
                                    <Link
                                        className="desktop_hero_banner_link"
                                        to={item.banner_link ? item.banner_link : "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={item?.title || "Open business banner"}
                                    />
                                    {item?.title && <strong>{item.title}</strong>}
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </section>
            )}

            {(verticalVideos.length > 0 || horizontalVideos.length > 0) && (
                <section className="business_video_panel">
                    <div className="business_section_head">
                        <span>{t("Stories")}</span>
                        <h2>{t("Business Videos")}</h2>
                    </div>

                    {verticalVideos.length > 0 && (
                        <div className="business_vertical_grid">
                            {verticalVideos.map((item) => {
                                const videoId = `${item.type}-${item.id}`;
                                const isActiveVideo = activeVideoId === videoId;

                                return (
                                    <div key={videoId} className="business_video_card business_video_vertical">
                                        <video
                                            src={item.video_url}
                                            autoPlay
                                            muted={!isActiveVideo}
                                            loop
                                            playsInline
                                            preload="metadata"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleVideoSound(videoId)}
                                            className="business_sound_btn"
                                            aria-label={isActiveVideo ? "Mute video" : "Unmute video"}
                                        >
                                            {isActiveVideo ? <FaVolumeUp /> : <FaVolumeMute />}
                                        </button>
                                        {item?.title && <strong>{item.title}</strong>}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {horizontalVideos.length > 0 && (
                        <div className="business_horizontal_grid">
                            {horizontalVideos.map((item) => {
                                const videoId = `${item.type}-${item.id}`;
                                const isActiveVideo = activeVideoId === videoId;

                                return (
                                    <div key={videoId} className="business_video_card business_video_horizontal">
                                        <video
                                            src={item.video_url}
                                            autoPlay
                                            muted={!isActiveVideo}
                                            loop
                                            playsInline
                                            preload="metadata"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleVideoSound(videoId)}
                                            className="business_sound_btn"
                                            aria-label={isActiveVideo ? "Mute video" : "Unmute video"}
                                        >
                                            {isActiveVideo ? <FaVolumeUp /> : <FaVolumeMute />}
                                        </button>
                                        {item?.title && <strong>{item.title}</strong>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            )}

            <section className="business_register_panel">
                <div className="business_section_head">
                    <span>{t("Partner")}</span>
                    <h2>{t("Register With Us")}</h2>
                </div>

                {businessOptions.length > 0 && (
                    <div className="business_register_grid">
                        {businessOptions.map((item, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => window.open(item.link, "_blank")}
                                className="business_register_card"
                            >
                                <span className="business_img">
                                    <img src={item?.logo} alt="" />
                                </span>
                                <strong>{item.title}</strong>
                            </button>
                        ))}
                    </div>
                )}

                {businessForms.length > 0 && (
                    <div className="business_form_grid">
                        {businessForms.map((item, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => navigate(`/form/${item.slug}`)}
                                className="business_form_card"
                            >
                                <span className="business_img">
                                    <img src={item?.logo} alt="" />
                                </span>
                                <strong>{item.title}</strong>
                            </button>
                        ))}
                    </div>
                )}

                {businessOptionCount === 0 && (
                    <div className="business_empty_state">
                        <strong>{t("No business options available")}</strong>
                        <p>{t("Please check again later.")}</p>
                    </div>
                )}
            </section>
        </ThemedPageFrame>
    )
}

export default BusinessUser
/* jshint ignore:end */
